import cn from 'classnames';

import React, {
  PropsWithChildren,
  useEffect, useRef, useState
} from 'react';
import { ArrayOfReactElement } from '../common/Types';
import { idGenerator } from '../common/Utils';
import { ALERT_TYPE_MAP } from './constants';

interface AlertsProps {
  page?: number;
  alerts: ArrayOfReactElement<AlertProps>;
  onPageChange?: (newPage: number) => void;
}

export const Alerts = React.forwardRef<HTMLDivElement, AlertsProps>(
  (props, ref) => {
    const { alerts, onPageChange } = props;
    const totalAlerts = alerts.length;

    // Page Starts from 0
    const [page, setPage] = useState(props.page ?? 0);
    useEffect(() => {
      if (props.page !== undefined) {
        setPage(props.page);
      }
    }, [props.page]);

    const onPrevious = () => {
      const prevPage = page - 1 < 0 ? totalAlerts - 1 : page - 1;
      if (props.page !== undefined && onPageChange) {
        onPageChange(prevPage);
      } else {
        setPage(prevPage);
      }
    };
    const onNext = () => {
      const nextPage = page + 1 >= totalAlerts ? 0 : page + 1;
      if (props.page !== undefined && onPageChange) {
        onPageChange(nextPage);
      } else {
        setPage(nextPage);
      }
    };

    const renderAlerts = () => {
      return React.Children.map(alerts, (alert, index) => {
        if (alert) {
          return React.cloneElement(alert, {
            hidden: index !== page,
            appLevel: true
          });
        }
      });
    };

    const alertsType = (totalAlerts > 0 && alerts[page]?.props.type) || 'info';

    const className = cn('alerts', `alert-${alertsType}`);

    return (
      <div ref={ref} className={className}>
        <div className="alerts-pager">
          <div className="alerts-pager-control">
            <div className="alerts-page-down">
              <button className="alerts-pager-button" onClick={onPrevious}>
                <clr-icon shape="caret left" role="none" title="Previous" />
              </button>
            </div>
            <div className="alerts-pager-text">
              {page + 1} / {totalAlerts}
            </div>
            <div className="alerts-page-up">
              <button className="alerts-pager-button" onClick={onNext}>
                <clr-icon shape="caret right" role="none" title="Next" />
              </button>
            </div>
          </div>
        </div>
        {renderAlerts()}
      </div>
    );
  }
);

type AlertType = 'info' | 'success' | 'warning' | 'danger';

interface AlertProps {
  appLevel?: boolean;
  type?: AlertType;
  small?: boolean;
  hidden?: boolean;
  closable?: boolean;
  closed?: boolean; // If this is present then controlled element else uncontrolled
  items?: ArrayOfReactElement<AlertItemProps>;
  ariaLiveMsg?: string;
  onDismiss?: () => void;
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (props, ref) => {
    const {
      appLevel,
      closable,
      closed,
      hidden,
      items,
      onDismiss,
      small,
      ariaLiveMsg,
      type = 'info'
    } = props;

    const [dismissed, setDismissed] = useState(closed ?? false);
    const uid = useRef(`clr-alert-item-${idGenerator()()}`);

    useEffect(() => {
      if (closed !== undefined) {
        setDismissed(closed);
      }
      closed && removeFromAriaLive();
    }, [closed]);

    useEffect(() => {
      const ariaLiveTag = document.getElementById('aria-live-element');
      if (ariaLiveTag && ariaLiveMsg) {
        const alertElement = document.createElement('div');
        alertElement.setAttribute('id', `${uid.current}`);
        alertElement.innerHTML = `${ariaLiveMsg}`;
        ariaLiveTag.appendChild(alertElement);
      }
      return removeFromAriaLive;
    }, [ariaLiveMsg]);

    const removeFromAriaLive = () => {
      const ariaLiveTag = document.getElementById('aria-live-element');
      if (ariaLiveTag) {
        const alertNode = document.getElementById(`${uid.current}`);
        alertNode && ariaLiveTag.removeChild(alertNode);
      }
    };

    const [defaultIconShape, defaultIconTitle] = ALERT_TYPE_MAP[type];

    const renderAlertItems = () => {
      return React.Children.map(items, (item, index) => {
        if (item) {
          return React.cloneElement(item, {
            iconShape: item.props.iconShape || defaultIconShape,
            iconTitle: item.props.iconTitle || defaultIconTitle,
            key: item.key || index
          });
        }
      });
    };

    const dismissAlert = () => {
      if (closed === undefined) {
        setDismissed(true);
        removeFromAriaLive();
      }
      if (onDismiss) {
        onDismiss();
      }
    };

    const classNames = cn('alert', `alert-${type}`, {
      'alert-app-level': appLevel,
      'alert-hidden': hidden,
      'alert-sm': small
    });

    return !dismissed ? (
      <div ref={ref} className={classNames}>
        <div className="alert-items">{renderAlertItems()}</div>
        {closable && (
          <button
            type="button"
            className="close"
            aria-label="Close"
            onClick={dismissAlert}>
            <clr-icon aria-hidden="true" shape="close" role="none" />
          </button>
        )}
      </div>
    ) : null;
  }
);

type AlertItemProps = PropsWithChildren<{
  iconShape?: string;
  iconTitle?: string;
}>;

export const AlertItem = React.forwardRef<HTMLDivElement, AlertItemProps>(
  (props, ref) => {
    const { children, iconShape, iconTitle } = props;

    return (
      <div ref={ref} className="alert-item static" role="alert">
        <div className="alert-icon-wrapper">
          <clr-icon
            class="alert-icon"
            shape={iconShape}
            role="none"
            title={iconTitle}
          />
        </div>
        {typeof children === 'string' ? (
          <span className="alert-text">{children}</span>
        ) : (
          <span>{children}</span>
        )}
      </div>
    );
  }
);
